import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TransactionAmountType } from '../../../__generated__/globalTypes';
import { PRESERVE_SCROLL_STATE } from '../../../hooks/scroll/usePaneScrollRestoration';
import useDebouncedValue from '../../../hooks/useDebouncedValue';

/**
 * Table view state mirrored to the URL, the way `start` / `end` are through
 * `DateRangeProvider`: a record page opened from a table goes back in history
 * to close, which remounts the table, so anything worth keeping across that
 * round trip has to live in the origin entry's URL. Every write replaces the
 * entry — view state is not a destination — and carries `PRESERVE_SCROLL_STATE`
 * so the pane scroller holds its position.
 */
export const TABLE_PARAMS = {
  accounts: 'accounts',
  amountTypes: 'amountTypes',
  search: 'q',
  toggledStreams: 'toggledStreams',
} as const;

export const SEARCH_DEBOUNCE_MS = 250;

/** Comma-separated list param; blank tokens are dropped. */
export const parseListParam = (value: string | null): string[] =>
  value === null
    ? []
    : value
        .split(',')
        .map((token) => token.trim())
        .filter((token) => token.length > 0);

/** The inverse of `parseListParam`; `null` means "delete the param". */
export const serializeListParam = (values: readonly string[]): string | null => {
  const tokens = values.filter((value) => value.length > 0);
  return tokens.length > 0 ? tokens.join(',') : null;
};

const AMOUNT_TYPES = new Set<string>(Object.values(TransactionAmountType));

const isTransactionAmountType = (value: string): value is TransactionAmountType => AMOUNT_TYPES.has(value);

/** The next value of one param, computed from the value the live URL holds. */
type ParamUpdater = (current: string | null) => string | null;

type WriteSearchParam = (name: string, next: string | null | ParamUpdater) => void;

// Shared by every writer instance on the page (a merchant pane mounts one per
// stream), so writes within one tick chain no matter which instance made them.
let latestSearch = '';

/**
 * A replace-only writer for one search param. Successive writes within one
 * tick build on each other rather than on the render-time URL — react-router's
 * functional `setSearchParams` reads the last rendered snapshot, so two calls
 * from one handler (the table's "Clear all") would drop one. An updater reads
 * the param the same way, so a value derived from the old one chains too.
 */
const useWriteSearchParam = (): WriteSearchParam => {
  const navigate = useNavigate();
  const location = useLocation();

  React.useLayoutEffect(() => {
    latestSearch = location.search;
  }, [location.search]);

  return React.useCallback(
    (name, next) => {
      const params = new URLSearchParams(latestSearch);
      const current = params.get(name);
      const value = typeof next === 'function' ? next(current) : next;
      if (current === value) {
        return;
      }
      if (value === null) {
        params.delete(name);
      } else {
        params.set(name, value);
      }
      const search = `?${params.toString()}`;
      latestSearch = search;
      void navigate(search, { replace: true, state: PRESERVE_SCROLL_STATE });
    },
    [navigate]
  );
};

export interface ITransactionsTableParams {
  accountIds: string[];
  setAccountIds: (ids: string[]) => void;
  amountTypes: TransactionAmountType[];
  setAmountTypes: (types: TransactionAmountType[]) => void;
  /** What the search box shows — local, so typing never waits on a navigation. */
  searchText: string;
  setSearchText: (text: string) => void;
  /** The `q` param: `searchText` debounced by `SEARCH_DEBOUNCE_MS`, and what the query filters on. */
  searchQuery: string;
}

/**
 * `TransactionsTable`'s account / amount-type filters and search text, read
 * from and written to the URL.
 *
 * The search box keeps its own text: react-router commits navigations inside
 * `startTransition`, and a controlled input bound to that drops keystrokes. The
 * debounced text is what reaches the URL, and a `q` that changes underneath the
 * box without the box having written it (a history move) re-seeds it.
 *
 * @returns The URL-backed account ids, amount types, and `q` with their
 * setters, plus the box's own text and its setter.
 */
export const useTransactionsTableParams = (): ITransactionsTableParams => {
  const location = useLocation();
  const write = useWriteSearchParam();
  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const accountIds = React.useMemo(() => parseListParam(params.get(TABLE_PARAMS.accounts)), [params]);
  const amountTypes = React.useMemo(
    () => parseListParam(params.get(TABLE_PARAMS.amountTypes)).filter(isTransactionAmountType),
    [params]
  );
  const searchQuery = params.get(TABLE_PARAMS.search) ?? '';

  const [searchText, setSearchText] = React.useState(searchQuery);
  const debouncedSearchText = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);
  const lastWrittenQuery = React.useRef(searchQuery);

  React.useEffect(() => {
    if (searchQuery !== lastWrittenQuery.current) {
      lastWrittenQuery.current = searchQuery;
      setSearchText(searchQuery);
    }
  }, [searchQuery]);

  React.useEffect(() => {
    if (debouncedSearchText !== lastWrittenQuery.current) {
      lastWrittenQuery.current = debouncedSearchText;
      write(TABLE_PARAMS.search, debouncedSearchText === '' ? null : debouncedSearchText);
    }
  }, [debouncedSearchText, write]);

  const setAccountIds = React.useCallback(
    (ids: string[]) => write(TABLE_PARAMS.accounts, serializeListParam(ids)),
    [write]
  );
  const setAmountTypes = React.useCallback(
    (types: TransactionAmountType[]) => write(TABLE_PARAMS.amountTypes, serializeListParam(types)),
    [write]
  );

  return { accountIds, setAccountIds, amountTypes, setAmountTypes, searchText, setSearchText, searchQuery };
};

export interface IToggledStreamsParam {
  /** Whether the stream's collapse state is flipped from its default. */
  isToggled: (streamId: string) => boolean;
  toggle: (streamId: string) => void;
}

/**
 * Which recurring-stream tables are flipped from their default collapse state.
 * Storing the flip rather than the state keeps the URL empty until someone
 * clicks, and still reads correctly for every stream on the page.
 *
 * @returns `isToggled` to read a stream's flip and `toggle` to write it.
 */
export const useToggledStreamsParam = (): IToggledStreamsParam => {
  const location = useLocation();
  const write = useWriteSearchParam();
  const toggled = React.useMemo(
    () => new Set(parseListParam(new URLSearchParams(location.search).get(TABLE_PARAMS.toggledStreams))),
    [location.search]
  );

  const isToggled = React.useCallback((streamId: string) => toggled.has(streamId), [toggled]);
  const toggle = React.useCallback(
    (streamId: string) => {
      write(TABLE_PARAMS.toggledStreams, (current) => {
        const next = new Set(parseListParam(current));
        if (next.has(streamId)) {
          next.delete(streamId);
        } else {
          next.add(streamId);
        }
        return serializeListParam([...next]);
      });
    },
    [write]
  );

  return { isToggled, toggle };
};
